import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import { UserDataContext } from "./UserDataContext";
import { MenuItem, InputAdornment } from "@material-ui/core";
import { roles } from "../Mappings";
import { LinehaulFloatingDataReducer } from "../Reducers/LinehaulFloatingDataReducer";
import { statuses } from "../Mappings";
import {
  displayMoney,
  getCurrentBranchesObject,
  getWeekDates,
} from "../GlobalFunctions";

export const LinehaulFloatingDataContext = createContext();

const LinehaulFloatingDataContextProvider = (props) => {
  const { userData } = useContext(UserDataContext);
  const [linehaulFloatingData, dispatch] = useReducer(
    LinehaulFloatingDataReducer,
    {}
  );

  console.log("Rerender");

  const createFloatingModel = useCallback(
    (params) => {
      const branchesObject = getCurrentBranchesObject(
        userData.branches,
        params.week
      );

      return {
        adjustment: {
          displayName: "Adjustment",
          displayValue: (value) =>
            `$${(Math.round(value * 100) / 100).toFixed(2)}`,
          validate: (value) => /^\d+(\.\d{1,12})?$/.test(value) || value === "",
          mapToRequestValue: (value) => value,
          textFieldProps: {
            name: "adjustment",
            InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
          },
        },
        km: {
          displayName: "Overall kilometers",
          displayValue: (value) => `${Math.round(value * 100) / 100}km`,
          validate: (value) => /^[0-9]{1,12}(\.[0-9]{1,4})?$/.test(value),
          mapToRequestValue: (value) => Number(value),
          textFieldProps: {
            name: "km",
          },
        },
        tripRate: {
          displayName: "Trip Rate",
          displayValue: (value) =>
            `$${(Math.round(value * 100) / 100).toFixed(2)}`,
          validate: (value) => value !== "",
          mapToRequestValue: (value) => Number(value),
          textFieldProps: {
            name: "tripRate",
            InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
            disabled: true,
          },
        },
        originBranchId: {
          displayName: "Origin Branch",
          displayValue: (value) => branchesObject[value].shortCode,
          validate: (value) => value !== "",
          mapToRequestValue: (value) => Number(value),
          textFieldProps: {
            name: "originBranchId",
            select: true,
            children: Object.keys(branchesObject).map((branchId) => (
              <MenuItem key={branchId} value={branchId}>
                {branchesObject[branchId].branchName}
              </MenuItem>
            )),
          },
        },
        destinationBranchId: {
          displayName: "Destination Branch",
          displayValue: (value) => branchesObject[value].shortCode,
          validate: (value) => value !== "",
          mapToRequestValue: (value) => Number(value),
          textFieldProps: {
            name: "destinationBranchId",
            select: true,
            children: Object.keys(branchesObject).map((branchId) => (
              <MenuItem key={branchId} value={branchId}>
                {branchesObject[branchId].branchName}
              </MenuItem>
            )),
          },
        },
        assignedBranchId: {
          displayName: "Assigned Branch",
          displayValue: (value) =>
            value ? branchesObject[value].shortCode : "Unassigned",
          validate: (value) => Boolean(value),
          mapToRequestValue: (value) => Number(value),
          textFieldProps: {
            name: "assignedBranchId",
            select: true,
            children: Object.keys(branchesObject).map((branchId) => (
              <MenuItem key={branchId} value={branchId}>
                {branchesObject[branchId].branchName}
              </MenuItem>
            )),
          },
        },
        tripCost: {
          displayName: "Trip Cost",
          displayValue: (value) => `$${displayMoney(value)}`,
          validate: (value) => value !== "",
          mapToRequestValue: (value) => Number(value),
          textFieldProps: {
            name: "tripCost",
            disabled: true,
            variant: "outlined",
            InputProps: { startAdornment: <InputAdornment>$</InputAdornment> },
          },
        },
        driverComments: {
          displayName: "Driver Comments",
          displayValue: (value) => value,
          validate: (value) => true,
          mapToRequestValue: (value) => value,
          textFieldProps: {
            name: "driverComments",
            placeholder: "Provide a brief description",
            multiline: true,
            variant: "outlined",
            rows: 3,
            rowsMax: 3,
          },
        },
        adminComments: {
          displayName: "Admin Comments",
          displayValue: (value) => value,
          validate: (value) => true,
          mapToRequestValue: (value) => value,
          textFieldProps: {
            name: "adminComments",
            placeholder: "Provide a brief description",
            multiline: true,
            variant: "outlined",
            rows: 3,
            rowsMax: 3,
          },
        },
        managerComments: {
          displayName: "Manager Comments",
          displayValue: (value) => value,
          validate: (value) => true,
          mapToRequestValue: (value) => value,
          textFieldProps: {
            name: "managerComments",
            placeholder: "Provide a brief description",
            multiline: true,
            variant: "outlined",
            rows: 3,
            rowsMax: 3,
          },
        },
        approvalState: {
          displayName: "Approval State",
          displayValue: (value) => statuses[value],
          validate: (value) => true,
          mapToRequestValue: (value) => value,
          textFieldProps: {
            name: "approvalState",
          },
        },
      };
    },
    [userData.branches]
  );

  const floatingColumns = {
    [roles.ownerDriver]: [
      "km",
      "tripRate",
      "originBranchId",
      "destinationBranchId",
      "tripCost",
      "driverComments",
    ],
    [roles.tollAdmin]: [
      "km",
      "tripRate",
      "originBranchId",
      "destinationBranchId",
      "tripCost",
      "assignedBranchId",
      "adminComments",
    ],
  }[userData.roleId];

  const comments = {
    [roles.tollAdmin]: "adminComments",
    [roles.ownerDriver]: "driverComments",
    [roles.tollManager]: "managerComments",
  }[userData.roleId];

  const defaultFloating = {
    id: "",
    driverId: "",
    eventDateTime: "",
    originBranchId: "",
    destinationBranchId: "",
    assignedBranchId: undefined,
    km: "",
    driverComments: "",
    adminComments: "",
    managerComments: "",
  };

  return (
    <LinehaulFloatingDataContext.Provider
      value={{
        createFloatingModel,
        floatingColumns,
        defaultFloating,
        linehaulFloatingData,
        dispatch,
        comments,
      }}
    >
      {props.children}
    </LinehaulFloatingDataContext.Provider>
  );
};

export default LinehaulFloatingDataContextProvider;
